// src/pages/signature/SignaturePartiesPage.tsx
// Party registry — browse, create, edit, delete signing parties, and record
// a manual signature evidence entry for a party.
// See docs/ux/signature-ia.md section 2. Admin-gated (AdminRoute, in App.tsx).

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n, useSignature } from '@react/hooks';
import { Button, ErrorAlert, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PartyDialog } from '@/features/signature/PartyDialog';
import { RecordSignatureDialog } from '@/features/signature/RecordSignatureDialog';
import type { Party } from '@core/types/signature';
import { enumSuffix, partyStatusVariant } from '@/features/signature/logic';

export const SignaturePartiesPage = observer(() => {
  const { t } = useI18n();
  const store = useSignature();
  const [parties, setParties] = useState<Party[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [selected, setSelected] = useState<Party | null>(null);
  const [mode, setMode] = useState<'view' | 'edit' | 'record' | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Party | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const load = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const r = await store.ListParties();
      setParties(r.items ?? []);
    } catch (err) {
      setLoadError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = async (p: Party) => {
    if (selected?.entity?.id === p.entity?.id && mode === 'view') {
      setSelected(null);
      setMode(null);
      return;
    }
    if (!p.entity?.id) return;
    const r = await store.GetParty(p.entity.id);
    setSelected(r.party ?? p);
    setMode('view');
  };

  const handleDelete = async () => {
    if (!deleteTarget?.entity?.id) return;
    setIsDeleting(true);
    try {
      await store.DeleteParty(deleteTarget.entity.id);
      setDeleteTarget(null);
      if (selected?.entity?.id === deleteTarget.entity.id) { setSelected(null); setMode(null); }
      load();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{t('signature.parties.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('signature.parties.subtitle')}</p>
        </div>
        <Button size="sm" onClick={() => { setShowCreate((v) => !v); setSelected(null); setMode(null); }}>
          {t('signature.parties.createTitle')}
        </Button>
      </div>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {loadError != null && <ErrorAlert error={loadError} dismissible onDismiss={() => setLoadError(null)} />}

      {showCreate && <PartyDialog onSaved={() => { setShowCreate(false); load(); }} onCancel={() => setShowCreate(false)} />}

      <Table striped hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('signature.field.displayName')}</TableHead>
            <TableHead>{t('signature.field.role')}</TableHead>
            <TableHead>{t('signature.field.status')}</TableHead>
            <TableHead>{t('signature.verify.targetType')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
          ) : parties.length === 0 ? (
            <TableEmpty colSpan={5} message={t('signature.parties.empty')} />
          ) : (
            parties.map((p) => (
              <TableRow key={p.entity?.id} className="cursor-pointer" onClick={() => handleSelect(p)}>
                <TableCell>{p.display_name || p.external_email || '—'}</TableCell>
                <TableCell>{t(`signature.role.${enumSuffix(p.role, 'PARTY_ROLE_')}`)}</TableCell>
                <TableCell><StatusBadge variant={partyStatusVariant(p.status)} label={t(`signature.partyStatus.${enumSuffix(p.status, 'PARTY_STATUS_')}`)} size="sm" /></TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.target?.type_name}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setSelected(p); setMode('edit'); }}>{t('common.edit')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setSelected(p); setMode('record'); }}>{t('signature.parties.recordSignature')}</Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setDeleteTarget(p)}>{t('common.delete')}</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {selected && mode === 'view' && (
        <div className="rounded-lg border border-border p-4">
          <h3 className="mb-2 text-sm font-semibold text-foreground">{selected.display_name}</h3>
          <pre className="max-h-80 overflow-auto rounded-md bg-muted/40 p-3 text-xs">{JSON.stringify(selected, null, 2)}</pre>
        </div>
      )}
      {selected && mode === 'edit' && (
        <PartyDialog party={selected} onSaved={() => { setMode(null); setSelected(null); load(); }} onCancel={() => setMode(null)} />
      )}
      {selected && mode === 'record' && (
        <RecordSignatureDialog party={selected} onSaved={() => { setMode(null); setSelected(null); }} onCancel={() => setMode(null)} />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('signature.parties.deleteConfirmTitle')}
        description={t('signature.parties.deleteConfirmDescription')}
        destructive
        isConfirming={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
});

export default SignaturePartiesPage;
